import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import fragment from './shaders/fragment.glsl';
import fragment2 from './shaders/fragment2.glsl';
import vertex from './shaders/vertex.glsl';
import vertex2 from './shaders/vertex2.glsl';
import noise from './shaders/noise.glsl';
import imagesLoaded from 'imagesloaded';
import gsap from 'gsap';
import FontFaceObserver from 'fontfaceobserver';
import ocean from '../img/1.jpg';
import Scroll from './scroll';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
// import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';



export default class Sketch 
{
	constructor(canvas) 
	{
		this.scene = new THREE.Scene();
		this.scene.background = new THREE.Color("rgb(237, 237, 237)");

		this.container = canvas.dom;
		this.width = this.container.offsetWidth;
		this.height = this.container.offsetHeight;

		this.renderer = new THREE.WebGLRenderer({
			antialias: true,
			// alpha:true,
		});

		this.renderer.setSize(this.width, this.height);

		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio,2));

		this.container.appendChild(this.renderer.domElement);

		this.camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 100, 200000);
		this.camera.fov = 2 * Math.atan(this.height / 2 / 600) * (180 / Math.PI);
		this.camera.position.z = 600;
		this.controls = new OrbitControls(this.camera, this.renderer.domElement);
		this.controls.enableDamping = true;
		this.controls.dampingFactor = 0.05;
		this.time = 0;

		this.images = [ ...document.querySelectorAll('img') ];

		const fontOpen = new Promise((resolve) => {
			new FontFaceObserver('Open Sans').load().then(() => {
				resolve();
			});
		});
		const fontPlayfair = new Promise((resolve) => {
			new FontFaceObserver('Playfair Display').load().then(() => {
				resolve();
			});
		});

		// Preload images
		const preloadImages = new Promise((resolve, reject) => {
			imagesLoaded(document.querySelectorAll('img'), { background: true }, resolve);
		});

		let allDone = [ fontOpen, fontPlayfair, preloadImages ];
		this.currentScroll = 0;
		this.previousScroll = 0;
		this.raycaster = new THREE.Raycaster();
		this.mouse = new THREE.Vector2();

		Promise.all(allDone).then(() =>
		{

			this.addImages();
			this.setPosition();
			this.scroll = new Scroll();
			this.mouseMovement();
	
			this.resize();
			this.setupResize();
			this.addObjects();
			this.composerPass();
			this.render();
			

			// window.addEventListener('scroll',()=>{
			// 	this.currentScroll=(window.scrollY);
			// 	this.setPosition();
			// })

		});


	}

	composerPass()
	{
		this.composer = new EffectComposer(this.renderer);
		this.renderPass = new RenderPass(this.scene, this.camera);
		this.composer.addPass(this.renderPass);
	
		//custom shader pass
		var counter = 0.0;
		this.myEffect = {
		  uniforms: {
			"tDiffuse": { value: null },
			"scrollSpeed": { value:null},
			"time": { value:null},
		  },
		  vertexShader: `
		  varying vec2 vUv;
		  void main() {
			vUv = uv;
			gl_Position = projectionMatrix 
			  * modelViewMatrix 
			  * vec4( position, 1.0 );
		  }
		  `,
		  fragmentShader: `
		  uniform sampler2D tDiffuse;
		  uniform float scrollSpeed;
		  uniform float time;
		  varying vec2 vUv;
		  ${noise}


		  void main()
		  {
			vec2 newUV = vUv;
			float area = smoothstep(0.4,0.,vUv.y);
			float area2 = smoothstep(1.,0.85,vUv.y)*2.-1.;
			float noise = 0.9*(cnoise(vec3(vUv*20.,time/5.))+1.);
			area = pow(area,8.);

			float n = smoothstep(0.5,0.51,noise+area2);
			newUV.x-=(vUv.x-0.5)*0.2*area*scrollSpeed;
			gl_FragColor = texture2D( tDiffuse, newUV);
			// gl_FragColor = vec4(n,0.,0.,1.);
			 gl_FragColor = mix(vec4(0.93),texture2D(tDiffuse, newUV),n);


		  }
		  `
		}
		
		this.customPass = new ShaderPass(this.myEffect);
		this.customPass.renderToScreen = true;
		
		this.composer.addPass(this.customPass);
	}

	setupResize()
	 {
		window.addEventListener('resize', this.resize.bind(this));
	}

	resize() 
	{
		this.width = this.container.offsetWidth;
		this.height = this.container.offsetHeight;
		this.renderer.setSize(this.width, this.height);
		this.camera.aspect = this.width / this.height;
		// this.addObjects();
		this.camera.updateProjectionMatrix();
	}

	addImages() {
		this.material = new THREE.ShaderMaterial({
			uniforms: {
				// color:{value:0xff0000},
				time: { value: 0 },
				uImage: { value: 0 },
				hover: { value: new THREE.Vector2(0.5, 0.5) },
				hoverState: { value: 0}
				// oceanTexture: { value: new THREE.TextureLoader().load(ocean) }
			},
			side: THREE.DoubleSide,
			fragmentShader: fragment,
			vertexShader: vertex
			// wireframe: true
		});

		this.materials = [];

		this.imageStore = this.images.map((img) =>
		 {
			let bounds = img.getBoundingClientRect();

			let geometry = new THREE.PlaneBufferGeometry(1, 1, 50, 50);
			let textures = new THREE.Texture(img);
			textures.needsUpdate = true;

			let material = this.material.clone();

			img.addEventListener('mouseenter', () => {
				gsap.to(material.uniforms.hoverState, {
					duration: 1,
					value: 1
				});
			});

			img.addEventListener('mouseout', () => {
				gsap.to(material.uniforms.hoverState, {
					duration: 1.2,
					value: 0
				});
			});

			this.materials.push(material);

			material.uniforms.uImage.value = textures;

			let mesh = new THREE.Mesh(geometry, material);
			mesh.scale.set(bounds.width,bounds.height,1);
			this.scene.add(mesh);

			return {
				img: img,
				mesh: mesh,
				top: bounds.top,
				left: bounds.left,
				width: bounds.width,
				height: bounds.height
			};
		});
	}

	mouseMovement() {
		window.addEventListener(
			'mousemove',
			(event) => {
				this.mouse.x = event.clientX / this.width * 2 - 1;
				this.mouse.y = -(event.clientY / this.height) * 2 + 1;

				this.raycaster.setFromCamera(this.mouse, this.camera);

				const intersects = this.raycaster.intersectObjects(this.scene.children);

				if (intersects.length > 0) {
					let obj = intersects[0].object;
					obj.material.uniforms.hover.value = intersects[0].uv;
				}
			},
			false
		);
	}



	setPosition()
	{
		this.imageStore.forEach((o) => {

			o.mesh.position.y = this.currentScroll -o.top + this.height/2 - o.height/2;
            o.mesh.position.x = o.left - this.width/2 + o.width/2;
		});
	}

	
	addObjects() {
		this.material = new THREE.ShaderMaterial({
			uniforms: {
				time: { value: 0 },
				oceanTexture: { value: new THREE.TextureLoader().load(ocean) }
			},
			side: THREE.DoubleSide,
			fragmentShader: fragment2,
			vertexShader: vertex2,
			wireframe: true
		});

		this.geometry = new THREE.PlaneBufferGeometry(100, 100, 10, 10);
		// this.geometry = new THREE.SphereBufferGeometry(900, 40, 40);

		this.mesh = new THREE.Mesh(this.geometry, this.material);
		this.mesh.position.set(10, 10, -1000);
		// this.scene.add(this.mesh);



	}

	render() {
		this.time += 0.05;
		this.controls.update();
		this.scroll.render();



		this.currentScroll = this.scroll.scrollToRender;
		this.setPosition();
		this.previousScroll=this.scroll.scrollToRender;

		if(Math.round(this.scroll)!==Math.round(this.previousScroll))
		{	


			this.customPass.uniforms.scrollSpeed.value= this.scroll.speedTarget;
			this.customPass.uniforms.time.value = this.time;
	
	
			this.mesh.rotation.x+=0.01;
	
	
	
			this.materials.forEach((m) => {
				m.uniforms.time.value = this.time;
			});
			// this.renderer.setClearColor(new THREE.Color(0xff0000, 1.0));
			this.material.uniforms.time.value = this.time;
	
			// this.renderer.render(this.scene, this.camera);
			this.composer.render();
		}

		window.requestAnimationFrame(this.render.bind(this));
	}
}






new Sketch({
	dom: document.getElementById('container')
});




